import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { sendEmailVerification } from "firebase/auth";
import { auth } from "../firebase";
import { logout } from "../utils/session";
import AuthShell from "./AuthShell";

const EmailVerification: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const user = auth.currentUser;
  const [countdown, setCountdown] = useState(60);
  const [checking, setChecking] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      history.push("/login");
      return;
    }
    if (user.emailVerified) {
      history.push("/home");
      return;
    }
    const timer = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, [user, history]);

  const resend = async () => {
    if (!user || countdown > 0) return;
    try {
      await sendEmailVerification(user);
      setCountdown(60);
      setMsg(t("auth.verify.resent"));
    } catch (e: any) {
      setMsg(t("auth.errors.tooMany"));
    }
  };

  const check = async () => {
    if (!user) return;
    setChecking(true);
    try {
      await user.reload();
      if (auth.currentUser?.emailVerified) {
        // Force le rafraîchissement du token puis laisse le guard de App rediriger.
        await auth.currentUser.getIdToken(true);
        window.location.href = "/home";
      } else {
        setMsg(t("auth.verify.notYet"));
      }
    } finally {
      setChecking(false);
    }
  };

  return (
    <AuthShell
      title={t("auth.verify.title")}
      subtitle={user?.email || ""}
      icon="fa-envelope-open-text"
      alert={{
        open: Boolean(msg),
        header: t("common.information"),
        message: msg || "",
        onClose: () => setMsg(null),
        dismissLabel: t("common.close"),
      }}>
      <div className="card p-5 mb-5 text-sm text-gray-200 space-y-2">
        <p className="font-bold text-white">{t("auth.verify.instructions")}</p>
        <ol className="list-decimal ml-5 space-y-1">
          <li>{t("auth.verify.step1")}</li>
          <li>{t("auth.verify.step2")}</li>
          <li>{t("auth.verify.step3")}</li>
        </ol>
        <p className="text-xs text-gray-400 pt-2">{t("auth.verify.spam")}</p>
      </div>
      <div className="space-y-3">
        <button type="button" onClick={check} disabled={checking} className="btn-primary w-full">
          {checking ? t("common.loading") : t("auth.verify.check")}
        </button>
        <button type="button" onClick={resend} disabled={countdown > 0} className="btn-secondary w-full disabled:opacity-40">
          {countdown > 0 ? `${t("auth.verify.resend")} (${countdown}s)` : t("auth.verify.resend")}
        </button>
        <button type="button" onClick={() => logout()} className="w-full text-sm text-gray-400 py-2">
          {t("auth.verify.logout")}
        </button>
      </div>
    </AuthShell>
  );
};

export default EmailVerification;
